import { FAQQuestion } from './FAQPage';

interface FAQQuestionCardProps {
  question: FAQQuestion;
  onClick: () => void;
  priorities: Array<{ value: string; label: string; color: string }>;
  categories: Array<{ value: string; label: string }>;
}

export function FAQQuestionCard({ question, onClick, priorities, categories }: FAQQuestionCardProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'primary';
      case 'answered': return 'success';
      case 'closed': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'open': return 'Ouverte';
      case 'answered': return 'Répondue';
      case 'closed': return 'Fermée';
      default: return status;
    }
  };

  const getPriorityData = (priority: string) => {
    return priorities.find(p => p.value === priority) || priorities[0];
  };

  const getCategoryLabel = (category: string) => {
    const cat = categories.find(c => c.value === category);
    return cat?.label || category;
  };

  const priorityData = getPriorityData(question.priority);

  return (
    <div 
      className={`card h-100 cursor-pointer ${question.is_pinned ? 'border-warning' : ''}`}
      onClick={onClick}
      style={{ cursor: 'pointer', transition: 'all 0.2s' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {question.is_pinned && (
              <span className="badge bg-warning text-dark">
                <i className="fas fa-thumbtack me-1"></i>
                Épinglée
              </span>
            )}
            <span className={`badge bg-${getStatusColor(question.status)}`}>
              {getStatusLabel(question.status)}
            </span>
            <span className={`badge bg-${priorityData.color}`}>
              {priorityData.label}
            </span>
            <span className="badge bg-info">
              {getCategoryLabel(question.category)}
            </span>
          </div>
          <div className="text-muted small d-flex align-items-center gap-2">
            <span>
              <i className="fas fa-eye me-1"></i>
              {question.views_count}
            </span>
            <span>
              <i className="fas fa-comments me-1"></i>
              {question.answer_count}
            </span>
          </div>
        </div>

        <h5 className="card-title mb-2">
          {question.question.length > 120 
            ? `${question.question.substring(0, 120)}...` 
            : question.question}
        </h5>

        <div className="d-flex justify-content-between align-items-center text-muted small">
          <div>
            <i className="fas fa-user me-1"></i>
            Posée par <strong>{question.username}</strong>
            {question.answer_count > 0 && (
              <span className="badge bg-success ms-2">
                <i className="fas fa-check me-1"></i>
                Répondue
              </span>
            )}
          </div>
          <div>
            <i className="fas fa-clock me-1"></i>
            {formatDate(question.created_at)}
          </div>
        </div>

        {question.has_official_answer && (
          <div className="mt-2">
            <span className="badge bg-success">
              <i className="fas fa-check me-1"></i>
              Réponse officielle disponible
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
